import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Card from '../components/Card';
import Button from '../components/Buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import { Plus, FileText, Shield, Trash2, RefreshCw, BarChart2, Upload, Users, Eye } from 'lucide-react';
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import { useVerticals } from "../contexts/VerticalsContext";
import TPRMVendorSelectModal from "../components/TPRMVendorSelectModal";
import OrgAssignAssessModal from "../components/OrgAssignAssessModal";
import TPRMRespondentsModal from "../components/TPRMRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import { useRBAC } from "../hooks/useRBAC";
import { useURLState } from "../hooks/useURLState";
import CreateBusinessUnitModal from './CreateBusinessUnitModal';
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import AssessmentActivityLog from "../components/AssessmentActivityLog";

import { SHOW_DELETE_ASSESSMENT } from '../config';
const TPRMBusinessUnits = () => {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedBU, setSelectedBU] = useState(null);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  const [showNewBusinessUnitModal, setShowNewBusinessUnitModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [creatingAssessment, setCreatingAssessment] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEntityForAssign, setSelectedEntityForAssign] = useState(null);
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [businessUnitIdParam, setBusinessUnitIdParam] = useURLState('businessUnitId');
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);

  const [pageSize, setPageSize] = usePageSize('tprmBusinessUnits');
  const totalPages = Math.ceil(totalCount / pageSize);
  const listTopRef = useRef(null);
  const rightPanelRef = useRef(null);
  const fetchIdRef = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canManageBusinessUnit, canViewBusinessUnit, canAssessViaAssignment, canCreateDealOrBusinessUnit, authVersion, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = SHOW_DELETE_ASSESSMENT && canDeleteAssessments(); // super admin only, and the switch in config.js
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();
  const canManageRisksForBU = (buId) => canManageRisks || canManageBusinessUnit(buId);
  const canAddDealOrBU = canCreateDealOrBusinessUnit(selectedOrg?.organizationId);

  const { verticals } = useVerticals();

  // Reset assessment state when user/auth changes
  useEffect(() => {
    setAssessments([]);
    setSelectedBU(null);
  }, [authVersion]);

  useEffect(() => {
    fetchBusinessUnits();
    fetchVendors();
  }, [currentPage, pageSize, authVersion]);

  const fetchVendors = async () => {
    try {
      const data = await apiCall("GET", "/TPRMVendor/vendors?pageSize=100");
      setVendors(data.items || []);
    } catch (err) {
      setVendors([]);
    }
  };

  // Auto-select from URL param or navigation state
  useEffect(() => {
    if (businessUnits.length === 0) return;

    const autoSelectId = businessUnitIdParam || location.state?.selectedBusinessUnitId;
    if (autoSelectId) {
      const targetId = typeof autoSelectId === 'string' ? parseInt(autoSelectId) : autoSelectId;
      const target = businessUnits.find(bu => bu.businessUnitId === targetId);
      if (target) {
        setSelectedBU(target);
        fetchBUAssessments(target.businessUnitId);
      }
      if (location.state?.selectedBusinessUnitId) {
        navigate('/TPRMBusinessUnits', { replace: true });
      }
    }
  }, [businessUnits]);

  const fetchBusinessUnits = async () => {
    setPaging(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      if (!org?.organizationId) {
        return;
      }

      const data = await apiCall("GET", `/businessUnits/summary?category=Vendor&page=${currentPage}&pageSize=${pageSize}`);

      const busWithRisk = (data.items || []).map((bu) => ({
        businessUnitId: bu.businessUnitId,
        businessUnitName: bu.businessUnitName,
        verticalId: bu.verticalId,
        domain: bu.domain,
        vendorCount: bu.applicationCount,
        RiskLevel: bu.riskLevel === "NONE" ? "No RISK" : bu.riskLevel,
        riskCounts: {
          CRITICAL: bu.riskCounts?.critical || 0,
          HIGH: bu.riskCounts?.high || 0,
          MEDIUM: bu.riskCounts?.medium || 0,
          LOW: bu.riskCounts?.low || 0,
        },
        latestAssessment: bu.assessmentId ? {
          tprmAssessmentID: bu.assessmentId,
          tprmAssignedToUserId: bu.assignedToUserId,
          tprmAssignedToUserName: bu.assignedToUserName,
          tprmStatus: bu.status,
        } : null,
      }));

      // Backend /businessUnits/summary already applies RBAC BU-scoping and returns a scoped
      // totalCount, so trust the server response directly. Re-filtering client-side broke
      // pagination for BU-scoped users by clobbering totalCount with the current page's length.
      setBusinessUnits(busWithRisk);
      setTotalCount(data.totalCount || 0);
    } catch (error) {} finally {
      setLoading(false);
      setPaging(false);
    }
  };

  const fetchBUAssessments = async (businessUnitId) => {
    const thisFetchId = ++fetchIdRef.current;
    setAssessmentsLoading(true);
    try {
      const data = await apiCall("GET", `/TPRMAssessments/summary?businessUnitId=${businessUnitId}`);
      if (thisFetchId !== fetchIdRef.current) return;

      const normalized = (Array.isArray(data) ? data : []).map((a) => ({
        ...a,
        hasRisks: a.totalRisks > 0,
        riskCounts: {
          CRITICAL: a.riskCounts?.critical || 0,
          HIGH: a.riskCounts?.high || 0,
          MEDIUM: a.riskCounts?.medium || 0,
          LOW: a.riskCounts?.low || 0,
        },
      }));

      setAssessments(normalized);
    } catch (err) {
      if (thisFetchId !== fetchIdRef.current) return;
    } finally {
      if (thisFetchId === fetchIdRef.current) {
        setAssessmentsLoading(false);
      }
    }
  };

  const handleVendorCreatedWithAssessment = async (newVendor) => {
    setCreatingAssessment(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));

      const assessmentPayload = {
        BusinessUnitId: selectedBU.businessUnitId,
        OrganizationId: org.organizationId,
        VendorId: newVendor.vendorId,
        RegionId: newVendor.regionId || null,
        VerticalId: newVendor.verticalId || null,
        TPRMAssessmentType: "Initial",
        TPRMFramework: "Default",
        TPRMAssessmentDate: new Date().toISOString(),
        TPRMStatus: "in_progress",
        TPRMProgress: 0,
      };

      await apiCall("POST", "/TPRMAssessments", assessmentPayload);
      setShowNewVendorModal(false);
      fetchBUAssessments(selectedBU.businessUnitId);
      fetchBusinessUnits();
    } catch (err) {
      alert("Vendor created, but assessment creation failed. You can assign manually.");
      setShowNewVendorModal(false);
      fetchBUAssessments(selectedBU.businessUnitId);
      fetchBusinessUnits();
    } finally {
      setCreatingAssessment(false);
    }
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/tprmAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchBUAssessments(selectedBU.businessUnitId);
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
        await apiCall('POST', `/tprmAssessments/${assessmentToReassess}/reassess`);
        setShowReassessModal(false);
        setAssessmentToReassess(null);
        navigate(`/TPRMAssessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
        setReassessing(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Business Unit Vendor Management</h1>
          <p className="mt-2 text-gray-600">
            Manage Business Unit Vendors for security risk assessment
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[43fr_57fr] gap-8">
          {/* Business Units */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Business Units</h2>
              {canAddDealOrBU && (
                <Button size="sm" onClick={() => setShowNewBusinessUnitModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Business Unit
                </Button>
              )}
            </div>

            <div className="space-y-3" ref={listTopRef}>
              {businessUnits.map((bu) => (
                <div
                  key={bu.businessUnitId}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedBU?.businessUnitId === bu.businessUnitId
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    setSelectedBU(bu);
                    setBusinessUnitIdParam(bu.businessUnitId);
                    fetchBUAssessments(bu.businessUnitId);
                    rightPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-content-primary">{bu.businessUnitName}</h3>
                      <p className="text-sm text-gray-600">{verticals.find(v => v.verticalId === bu.verticalId)?.verticalName || "N/A"}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {bu.vendorCount || 0} Vendors
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Risk Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">Risk Level</span>
                      <span className="text-xs font-medium text-content-primary">
                        {bu.RiskLevel || 'No RISK'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${bu.RiskLevel === 'CRITICAL'
                          ? 'bg-red-700'
                          : bu.RiskLevel === 'HIGH'
                            ? 'bg-orange-500'
                            : bu.RiskLevel === 'MEDIUM'
                              ? 'bg-yellow-500'
                              : bu.RiskLevel === 'LOW'
                                ? 'bg-green-500'
                                : 'bg-gray-400'
                          }`}
                        style={{
                          width: `${bu.RiskLevel === 'CRITICAL'
                            ? 90
                            : bu.RiskLevel === 'HIGH'
                              ? 75
                              : bu.RiskLevel === 'MEDIUM'
                                ? 50
                                : bu.RiskLevel === 'LOW'
                                  ? 25
                                  : 0
                            }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Risk Count Badges */}
                  {(bu.riskCounts.CRITICAL > 0 || bu.riskCounts.HIGH > 0 || bu.riskCounts.MEDIUM > 0 || bu.riskCounts.LOW > 0) && (
                    <div className="flex items-center mt-2 space-x-2">
                      {bu.riskCounts.CRITICAL > 0 && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                          {bu.riskCounts.CRITICAL} Critical
                        </span>
                      )}
                      {bu.riskCounts.HIGH > 0 && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                          {bu.riskCounts.HIGH} High
                        </span>
                      )}
                      {bu.riskCounts.MEDIUM > 0 && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                          {bu.riskCounts.MEDIUM} Medium
                        </span>
                      )}
                      {bu.riskCounts.LOW > 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                          {bu.riskCounts.LOW} Low
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPageParam}
              onPageSizeChange={setPageSize}
              scrollTargetRef={listTopRef}
              busy={paging}
            />
          </Card>

          {/* Vendor Assessments */}
          <Card ref={rightPanelRef}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Vendor Assessments</h2>
              <div className="flex gap-2">
                {selectedBU && assessments.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(`/TPRMPortfolio?businessUnitId=${selectedBU.businessUnitId}`)}
                  >
                    <BarChart2 className="h-4 w-4 mr-2" />
                    Portfolio Report
                  </Button>
                )}
                {selectedBU && canAddDealOrBU && (
                  <Button
                    size="sm"
                    onClick={() => setShowNewVendorModal(true)}
                    disabled={creatingAssessment}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {creatingAssessment ? "Creating..." : "New Vendor"}
                  </Button>
                )}
              </div>
            </div>

            {selectedBU ? (
              assessmentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : assessments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No vendor assessments associated with this business unit.
                </div>
              ) : (
              <div className="space-y-3">
                {[...assessments].sort((a, b) =>
                    (a.vendorName || '').localeCompare(b.vendorName || '')
                  ).map((assessment) => (
                    <div
                      key={assessment.tprmAssessmentID}
                      className="p-4 border border-gray-200 rounded-lg hover:border-gray-300"
                    >
                      <div>
                        <h3 className="font-medium text-content-primary">
                          {assessment.vendorName || `Vendor ID: ${assessment.vendorId}`}
                          <AssessmentStatusBadge status={assessment.tprmStatus} />
                        </h3>
                        <p className="text-sm text-gray-500">
                          {assessment.vendorDomain || ""}
                        </p>
                        {assessment.tprmAssignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{assessment.tprmAssignedToUserName}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex gap-4 items-start">
                        <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                        {canManageRisksForBU(selectedBU?.businessUnitId) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={assessment.tprmStatus === "completed"}
                            title={assessment.tprmStatus === "completed" ? "Assessment is completed" : ""}
                            onClick={() => {
                              setSelectedEntityForAssign(assessment);
                              setShowAssignModal(true);
                            }}
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {assessment.tprmAssignedToUserId ? "Reassign" : "Assign"}
                          </Button>
                        )}

                        {canManageRisksForBU(selectedBU?.businessUnitId) && assessment.tprmAssessmentID && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="whitespace-nowrap"
                            title="Manage additional respondents for this assessment"
                            onClick={() => setRespondentsAssessmentId(assessment.tprmAssessmentID)}
                          >
                            <Users className="h-4 w-4 mr-1" />
                            Respondents
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={
                            !canAssess(assessment.tprmAssignedToUserId, null, 'BUSINESS_UNIT', selectedBU?.businessUnitId, assessment.assigneeUserIds || []) ||
                            assessment.tprmStatus === "completed" ||
                            assessment.tprmStatus === "analyzing"
                          }
                          title={
                            (assessment.tprmStatus === "completed" ? "Assessment is completed"
                              : assessment.tprmStatus === "analyzing" ? "Assessment is being analyzed"
                              : "")
                              || getAssessDisabledReason(assessment.tprmAssignedToUserId, null, 'BUSINESS_UNIT', selectedBU?.businessUnitId, assessment.assigneeUserIds || [])
                              || ""
                          }
                          onClick={() => {
                            if (!assessment.tprmAssignedToUserId && !(assessment.assigneeUserIds || []).length) {
                              setShowUnassignedAlert(true);
                              return;
                            }
                            const vendor = vendors.find(v => v.vendorId === assessment.vendorId);
                            const vendorId = assessment.vendorId || vendor?.vendorId || 0;

                            if (!vendorId || vendorId === 0) {
                              alert("No vendor associated. Please create a new assessment.");
                              return;
                            }

                            const tprmPath = assessment.tprmAssessmentID
                              ? `/TPRMAssessment/${assessment.tprmAssessmentID}`
                              : '/TPRMAssessment';
                            navigate(tprmPath, {
                              state: {
                                vendorId: vendorId,
                                vendorName: assessment.vendorName || vendor?.vendorName,
                                assessmentId: assessment.tprmAssessmentID,
                                businessUnitId: selectedBU.businessUnitId,
                                businessUnitName: selectedBU.businessUnitName,
                                verticalId: vendor?.verticalId || selectedBU.verticalId,
                                regionId: vendor?.regionId,
                                subVerticalId: vendor?.subVerticalId,
                              }
                            });
                          }}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Assess
                        </Button>

                        {/* Viewing mirrors the backend's GetViewableAssessmentAsync: BU scope or assignment. */}
                        {assessment.tprmAssessmentID && assessment.tprmStatus === "completed" &&
                          (canViewBusinessUnit(selectedBU?.businessUnitId) || canAssessViaAssignment(assessment.tprmAssignedToUserId, assessment.assigneeUserIds || [])) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                            onClick={() => navigate(`/TPRMAssessment/${assessment.tprmAssessmentID}?step=review`)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Answers
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="secondary"
                          className="whitespace-nowrap"
                          title="Upload a vendor document for AI risk analysis"
                          onClick={() => {
                            const v = vendors.find(vnd => vnd.vendorId === assessment.vendorId);
                            const vendorId = assessment.vendorId || v?.vendorId || 0;
                            navigate('/TPRMDocumentAssessment', {
                              state: {
                                vendorId,
                                vendorName: assessment.vendorName || v?.vendorName,
                                businessUnitId: selectedBU.businessUnitId,
                              }
                            });
                          }}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Doc
                        </Button>

                        {assessment.hasRisks ? (
                          <Button
                            size="sm"
                            className="whitespace-nowrap"
                            onClick={() => navigate(`/VendorDashboard/${assessment.vendorId}`)}
                          >
                            <Shield className="h-4 w-4 mr-1" />
                            View Risks ({assessment.activeRisks ?? assessment.totalRisks})
                          </Button>
                        ) : (
                          <Button size="sm" disabled className="whitespace-nowrap">
                            <Shield className="h-4 w-4 mr-1" />
                            No Risks
                          </Button>
                        )}
                        {canManageRisksForBU(selectedBU?.businessUnitId) && assessment.tprmAssessmentID && (
                          <Button size="sm" variant="warning" className="whitespace-nowrap"
                            disabled={assessment.tprmStatus !== 'completed'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssessmentToReassess(assessment.tprmAssessmentID);
                              setShowReassessModal(true);
                            }}>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Reassess
                          </Button>
                        )}
                        {canDeleteAssessment && assessment.tprmAssessmentID && (
                          <Button size="sm" variant="danger" className="whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); setAssessmentToDelete(assessment.tprmAssessmentID); setShowDeleteModal(true); }}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>

                      {assessment.hasRisks ? (
                        <div className="mt-3">
                          {/* Risk badges */}
                          <div className="flex flex-wrap gap-2">
                            {assessment.riskCounts?.CRITICAL > 0 && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                {assessment.riskCounts.CRITICAL} Critical
                              </span>
                            )}
                            {assessment.riskCounts?.HIGH > 0 && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                                {assessment.riskCounts.HIGH} High
                              </span>
                            )}
                            {assessment.riskCounts?.MEDIUM > 0 && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                {assessment.riskCounts.MEDIUM} Medium
                              </span>
                            )}
                            {assessment.riskCounts?.LOW > 0 && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                {assessment.riskCounts.LOW} Low
                              </span>
                            )}
                          </div>

                          {/* Risk Score Bar */}
                          <div className="mt-2">
                            <div className="text-xs text-gray-500 mb-1">
                              Risk Score: {assessment.riskScore || 0}%
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div
                                className={`h-4 rounded-full ${
                                  (assessment.riskScore || 0) <= 40
                                    ? 'bg-green-500'
                                    : (assessment.riskScore || 0) <= 60
                                    ? 'bg-yellow-500'
                                    : (assessment.riskScore || 0) <= 80
                                    ? 'bg-orange-500'
                                    : 'bg-red-500'
                                }`}
                                style={{ width: `${assessment.riskScore || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {assessment.activeRisks ?? assessment.totalRisks ?? 0} Active Risks
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 text-xs text-gray-500">No Risk Data</div>
                      )}
                        </div>
                      </div>
                      <div className="flex justify-end mt-2">
                        <AssessmentActivityLog assessmentType="tprm" assessmentId={String(assessment.tprmAssessmentID)} hideQuestionEvents />
                      </div>
                    </div>
                  ))
                }
              </div>
              )
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Select a business unit to view assessments</p>
              </div>
            )}
          </Card>
        </div>
      </div >

      {/* Vendor Select Modal */}
      <TPRMVendorSelectModal
        isOpen={showNewVendorModal}
        onClose={() => setShowNewVendorModal(false)}
        onVendorSelected={handleVendorCreatedWithAssessment}
        vendors={vendors}
        existingVendorIds={assessments.map(a => a.vendorId)}
        onVendorCreated={(newVendor) => setVendors(prev => [...prev, newVendor])}
        businessUnitId={selectedBU?.businessUnitId}
      />

      {/* Assign Assessment Modal */}
      {showAssignModal && selectedEntityForAssign && (
        <OrgAssignAssessModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          organizationId={selectedOrg?.organizationId}
          vendorId={selectedEntityForAssign.vendorId}
          assessmentId={selectedEntityForAssign.tprmAssessmentID}
          assessmentType="tprm"
          hasExistingAssessment={true}
          refreshData={() => fetchBUAssessments(selectedBU.businessUnitId)}
        />
      )}

      <TPRMRespondentsModal
        isOpen={respondentsAssessmentId != null}
        onClose={() => setRespondentsAssessmentId(null)}
        assessmentId={respondentsAssessmentId}
        refreshData={() => fetchBUAssessments(selectedBU?.businessUnitId)}
      />

      {/* Create Business Unit Modal */}
      <CreateBusinessUnitModal
        isOpen={showNewBusinessUnitModal}
        onClose={() => setShowNewBusinessUnitModal(false)}
        onBusinessUnitCreated={() => { setCurrentPageParam(1); fetchBusinessUnits(); }}
      />

      <DeleteAssessmentModal isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
        onConfirm={handleDeleteAssessment} isDeleting={deleting} />

      <ReassessModal
        isOpen={showReassessModal}
        onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
        onConfirm={handleReassessAssessment}
        isReassessing={reassessing}
      />

      <AssessmentUnassignedModal
        isOpen={showUnassignedAlert}
        onClose={() => setShowUnassignedAlert(false)}
      />
    </div >
  );
};

export default TPRMBusinessUnits;
